// src/components/draft/OnTheClockTile.js
// Prominent "On the Clock" display showing last pick, current pick with timer, next pick
import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Trophy, ChevronLeft, ChevronRight, User, Timer, AlertTriangle, Pause } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import PlayerLink from '../PlayerLink';

const OnTheClockTile = ({
  picks = [],
  currentPick,
  timerData,
  isYourTurn,
  userTeamId,
  noTimer = false,
  isPaused = false
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(null);

  // Calculate last and next picks
  const { lastPick, nextPick } = useMemo(() => {
    if (!currentPick || !picks.length) {
      return { lastPick: null, nextPick: null };
    }

    const currentPickNumber = currentPick.overall_pick_number;
    const sortedPicks = [...picks].sort((a, b) =>
      (a.overall_pick_number || 0) - (b.overall_pick_number || 0)
    );

    // Find last completed pick (picked and before current)
    const lastPick = sortedPicks
      .filter(p => p.overall_pick_number < currentPickNumber && (p.league_player_id || p.player_name))
      .pop() || null;

    // Find next pick (after current)
    const nextPick = sortedPicks
      .find(p => p.overall_pick_number > currentPickNumber) || null;

    return { lastPick, nextPick };
  }, [picks, currentPick]);

  // Timer countdown
  useEffect(() => {
    if (!timerData?.expires_at) {
      setRemainingSeconds(null);
      return;
    }

    const calculateRemaining = () => {
      const expiresAt = typeof timerData.expires_at === 'string'
        ? new Date(timerData.expires_at).getTime()
        : timerData.expires_at;
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setRemainingSeconds(remaining);
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 1000);

    return () => clearInterval(interval);
  }, [timerData]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer color based on remaining time
  const getTimerColor = () => {
    if (remainingSeconds === null) return 'text-gray-400';
    if (remainingSeconds <= 10) return 'text-red-500';
    if (remainingSeconds <= 30) return 'text-yellow-500';
    return 'text-green-500';
  };

  const isLowTime = remainingSeconds !== null && remainingSeconds <= 30;
  const isExpired = remainingSeconds === 0;

  if (isPaused && currentPick) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-0 mb-4 overflow-hidden ring-2 ring-yellow-500/50`}>
        {/* Paused header bar */}
        <div className="px-4 py-2 bg-yellow-600">
          <div className="flex items-center justify-center gap-2">
            <Pause className="w-5 h-5 text-gray-900" />
            <span className="font-bold text-gray-900">DRAFT PAUSED</span>
          </div>
        </div>

        {/* Show current pick info so users know where the draft left off */}
        <div className="p-4 text-center">
          <div className="text-sm text-gray-400 mb-1">
            Pick #{currentPick.overall_pick_number} • Round {currentPick.round}
          </div>
          <div className="text-white font-medium">
            {currentPick.current_owner?.team_name || 'Team'}
          </div>
          <p className="text-yellow-500 text-sm mt-2">
            Waiting for commissioner to resume...
          </p>
        </div>
      </div>
    );
  }

  if (!currentPick) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-4 mb-4`}>
        <div className="text-center py-4">
          <Clock className="w-8 h-8 mx-auto text-gray-500 mb-2" />
          <p className={dynastyTheme.classes.text.neutralLight}>
            Waiting for draft to start...
          </p>
        </div>
      </div>
    );
  }

  const currentTeamIsUser = currentPick.current_owner_team_id === userTeamId;

  return (
    <div className={`${dynastyTheme.components.card.base} p-0 mb-4 overflow-hidden ${
      isYourTurn ? 'ring-2 ring-yellow-500 animate-pulse' : ''
    }`}>
      {/* Header bar */}
      <div className={`px-4 py-2 ${isYourTurn ? 'bg-yellow-500' : 'bg-gray-700'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className={`w-5 h-5 ${isYourTurn ? 'text-gray-900' : 'text-white'}`} />
            <span className={`font-bold ${isYourTurn ? 'text-gray-900' : 'text-white'}`}>
              {isYourTurn ? 'YOUR PICK!' : 'On The Clock'}
            </span>
          </div>
          {/* Timer in header */}
          {noTimer ? (
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${
              isYourTurn ? 'bg-gray-900/20' : 'bg-gray-800'
            }`}>
              <span className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight}`}>
                No Time Limit
              </span>
            </div>
          ) : timerData && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded ${
              isYourTurn ? 'bg-gray-900/20' : 'bg-gray-800'
            }`}>
              <Timer className={`w-4 h-4 ${getTimerColor()}`} />
              <span className={`font-mono font-bold text-lg ${getTimerColor()}`}>
                {formatTime(remainingSeconds)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Main content - 3 columns */}
      <div className="grid grid-cols-3 divide-x divide-gray-700">
        {/* Last Pick */}
        <div className="p-3">
          <div className="flex items-center gap-1 mb-2">
            <ChevronLeft className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500 uppercase tracking-wide">Last Pick</span>
          </div>
          {lastPick ? (
            <div>
              <div className="text-xs text-gray-400 mb-1">
                #{lastPick.overall_pick_number} • R{lastPick.round}
              </div>
              <div className="text-sm text-gray-300 truncate mb-1">
                {lastPick.current_owner?.team_name || lastPick.team_name || 'Team'}
              </div>
              <div className="flex items-center gap-1">
                <User className="w-3 h-3 text-green-400 flex-shrink-0" />
                <PlayerLink mlbId={lastPick.player?.ids?.mlb || null} name={lastPick.player?.info?.full_name || 'Player'} style={{ fontWeight: 500, fontSize: '0.875rem', color: '#ffffff' }} />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {lastPick.player?.info?.position || ''}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic">First pick</div>
          )}
        </div>

        {/* Current Pick (Center - Larger) */}
        <div className={`p-3 ${isYourTurn ? 'bg-yellow-500/10' : 'bg-gray-800/50'}`}>
          <div className="flex items-center justify-center gap-1 mb-2">
            <Trophy className={`w-4 h-4 ${isYourTurn ? 'text-yellow-500' : 'text-gray-400'}`} />
            <span className={`text-xs uppercase tracking-wide ${isYourTurn ? 'text-yellow-500' : 'text-gray-400'}`}>
              Current
            </span>
          </div>

          {/* Big pick number */}
          <div className={`text-center text-3xl font-bold mb-1 ${
            isYourTurn ? 'text-yellow-500' : 'text-white'
          }`}>
            #{currentPick.overall_pick_number}
          </div>

          <div className="text-center text-xs text-gray-400 mb-2">
            Round {currentPick.round}
          </div>

          {/* Team name */}
          <div className={`text-center text-sm font-medium truncate ${
            currentTeamIsUser ? 'text-yellow-400' : 'text-white'
          }`}>
            {currentPick.current_owner?.team_name || 'Team'}
          </div>

          {/* Low time warning (hidden in no-timer mode) */}
          {!noTimer && isLowTime && !isExpired && (
            <div className="flex items-center justify-center gap-1 mt-2 text-red-500 animate-pulse">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Time running out!</span>
            </div>
          )}

          {!noTimer && isExpired && (
            <div className="flex items-center justify-center gap-1 mt-2 text-red-500">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Time Expired!</span>
            </div>
          )}
        </div>

        {/* Next Pick */}
        <div className="p-3">
          <div className="flex items-center justify-end gap-1 mb-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide">On Deck</span>
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </div>
          {nextPick ? (
            <div className="text-right">
              <div className="text-xs text-gray-400 mb-1">
                #{nextPick.overall_pick_number} • R{nextPick.round}
              </div>
              <div className={`text-sm truncate mb-1 ${
                nextPick.current_owner_team_id === userTeamId ? 'text-blue-400 font-medium' : 'text-gray-300'
              }`}>
                {nextPick.current_owner?.team_name || nextPick.team_name || 'Team'}
              </div>
              {nextPick.current_owner_team_id === userTeamId && (
                <div className="text-xs text-blue-400">
                  You're next!
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic text-right">Last pick</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnTheClockTile;
